/**
 * 安全工具集（P-007 安全加固，2026-08-12）
 *
 * 集中管理 redirect 校验、JWT 解析、frame bust 等安全相关纯函数，
 * 便于单元测试 + 减少跨文件复制粘贴。
 */

/**
 * 安全 redirect 校验：拒绝 javascript: / data: / vbscript: 等危险协议。
 *
 * 规则：
 *   1. 必须以 `/` 开头（隐式排除所有非协议 URL，如 `//evil.com` 也被规则 2 拦截）
 *   2. 不能以 `//` 开头（避免 `//evil.com` 形式的开放重定向）
 *   3. 显式拒绝 `javascript:` / `data:` / `vbscript:` 协议前缀（大小写不敏感，
 *      防御性：即使前两条规则未来被改宽，也能在协议维度兜底）
 *
 * 用途：
 *   - LoginView 解析 `?redirect=` query（防 OAuth 回调 redirect 注入）
 *   - 任何从 URL / postMessage / 外部输入跳转的场景
 *
 * @param url 待校验的 redirect 目标
 * @returns true 表示安全可以跳转，false 表示拒绝（调用方 fallback 到首页）
 */
export function isSafeRedirect(url: string): boolean {
  if (!url) return false;
  if (!url.startsWith('/')) return false;
  if (url.startsWith('//')) return false;
  const lower = url.toLowerCase();
  if (lower.startsWith('javascript:')) return false;
  if (lower.startsWith('data:')) return false;
  if (lower.startsWith('vbscript:')) return false;
  return true;
}

/**
 * 解析 JWT payload（不验证签名，仅前端用于读取 exp 等字段）。
 *
 * JWT 结构：header.payload.signature，payload 是 base64url 编码的 JSON。
 * 本函数不做签名验证（前端无 secret，验证由后端完成），仅解码 payload
 * 供路由守卫判断 token 是否快过期。
 *
 * @returns payload 对象；token 格式异常时返回 null
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // atob 处理 base64（非 base64url），需把 -_ 还原为 +/
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * 计算 token 距离过期的剩余毫秒数。
 *
 * @returns 剩余毫秒（< 0 表示已过期）；token 无效或无 exp 字段时返回 NaN
 *          （调用方按"快过期 / 已失效"处理，跳登录页）
 */
export function getTokenRemainingMs(token: string): number {
  const payload = decodeJwtPayload(token);
  if (!payload) return Number.NaN;
  const exp = payload.exp;
  if (typeof exp !== 'number') return Number.NaN;
  return exp * 1000 - Date.now();
}

/**
 * Frame bust：检测页面是否被嵌入 iframe，若是则强制跳出。
 *
 * Clickjacking 防御兜底（CSP `frame-ancestors 'self'` 是主防线，
 * 本函数在 CSP 不被浏览器支持时兜底）。
 *
 * 调用时机：应用启动时（main.ts bootstrap 阶段）。
 * 使用 window.top!.location 而非 window.top.location 避免 TS strict null 检查报错。
 */
export function applyFrameBust(): void {
  try {
    if (window.top && window.top !== window.self) {
      // 跳出 iframe，回到顶层窗口
      window.top.location.href = window.self.location.href;
    }
  } catch {
    // 跨域 iframe 访问 window.top.location 会抛 SecurityError，
    // 这种情况说明已经被非同源页面嵌入，跳首页兜底
    window.self.location.href = '/';
  }
}

/**
 * 生产环境 HTTPS 强制。
 *
 * CSP meta 不能强制 HTTPS（CSP 只控制资源加载，不控制协议升级），
 * 此函数在应用启动时检查协议，HTTP 时跳 HTTPS。
 *
 * 调用时机：应用启动时（main.ts bootstrap 阶段）。
 * 仅在 PROD 生效，dev 模式（vite dev server）允许 HTTP。
 */
export function enforceHttpsInProd(): void {
  if (!import.meta.env.PROD) return;
  if (window.location.protocol === 'https:') return;
  // HTTP → HTTPS 强制（保留 host + path + query + hash）
  const httpsUrl = `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(httpsUrl);
}
